import React, { useEffect } from "react";
import { ArrowUp } from "lucide-react";
import Header from "../components/Header";
import SelectedWork from "../components/SelectedWork";
import { designer } from "../mock";
import { initTickAudio, playTick } from "../lib/tick";

const Portfolio = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    // If IntersectionObserver isn't supported, or fails to fire on iOS Safari
    // due to scroll-snap quirks, force everything visible after 400 ms.
    const safety = window.setTimeout(() => {
      els.forEach((el) => el.classList.add("in"));
    }, 400);

    if (!("IntersectionObserver" in window)) {
      return () => window.clearTimeout(safety);
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => obs.observe(el));
    return () => {
      obs.disconnect();
      window.clearTimeout(safety);
    };
  }, []);

  // Enable scroll-snap on the home page only (each project snaps to center)
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add("home-snap");
    return () => html.classList.remove("home-snap");
  }, []);

  // Tick on every snap target settling into view (skip the very first project on initial load, and any element marked data-no-tick)
  useEffect(() => {
    initTickAudio();
    const targets = document.querySelectorAll(".snap-start, .snap-end, .snap-project");
    const observed = new Set();
    let firstPass = true;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            if (observed.has(e.target)) return;
            observed.add(e.target);
            // Skip ticking the hero/top-of-page element
            if (e.target.dataset && e.target.dataset.noTick === "true") return;
            if (!firstPass) playTick();
          } else if (!e.isIntersecting) {
            observed.delete(e.target);
          }
        });
        firstPass = false;
      },
      { threshold: [0.6, 0.85] }
    );
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, []);

  const toTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <main className="bg-paper text-ink min-h-screen">
      <Header />

      {/* Hero — full viewport quote, sits between the fixed header and the first project */}
      <section data-no-tick="true" className="h-screen snap-start flex items-center justify-center px-10 md:px-20">
        <p
          data-testid="hero-quote"
          data-no-tick="true"
          className="serif text-center text-ink/90 tracking-wide font-light text-[22px]"
        >
          Form, Materiality and Mechanical Expression.
        </p>
      </section>

      <SelectedWork />

      {/* Back-to-top + line + two-liner about me */}
      <section className="px-10 md:px-20 py-16 md:py-20 snap-end">
        <div className="max-w-[1700px] mx-auto flex flex-col items-center text-center">
          <button
            onClick={toTop}
            aria-label="Back to top"
            className="group w-12 h-12 md:w-14 md:h-14 rounded-full border border-ink flex items-center justify-center hover:bg-ink hover:text-paper transition-colors duration-300"
          >
            <ArrowUp
              className="w-5 h-5 md:w-6 md:h-6 transition-transform duration-500 group-hover:-translate-y-0.5"
              strokeWidth={1.25}
            />
          </button>

          <div className="w-full max-w-5xl border-t border-ink mt-12 md:mt-16" />

          <p className="text-sm md:text-base font-light leading-relaxed max-w-xl mt-10 md:mt-12 text-ink">
            {designer.role} at {designer.company}, designing premium timepieces from {designer.location}.
          </p>
        </div>
      </section>
    </main>
  );
};

export default Portfolio;
